import Services from 'src/services/Services';

// Posta kodu arama altyapısı.
//
// Veri backend servisinden gelir. Servisler: Services.SearchPostalCodes,
// GetPostalCodeRegions, GetPostalCodeCities, GetPostalCodeCountries.
//
// Yanıt biçimi (dördü de aynı):
//   { status: true, result: { count, items: [...] } }
//
// Servis erişilemezse veya hata dönerse boş liste verilir; posta kodu alanı
// o durumda serbest metin gibi çalışır, ekran bozulmaz.

const DEFAULT_LIMIT = 50;

// Aynı sorgu için tekrar istek atılmasın.
const cache = new Map();
const pending = new Map();

/** Karşılaştırma öncesi normalize: "sw1a 1" -> "SW1A1" */
export const normalizePostalCode = (value) =>
  String(value || '').trim().toUpperCase().replace(/\s+/g, '');

/** Servis yanıtından items dizisini çıkarır. */
const readItems = (response) => {
  if (!response?.data?.status) return [];
  const result = response.data.result;
  const items = Array.isArray(result) ? result : result?.items;
  return Array.isArray(items) ? items : [];
};

/** İstek + önbellek + hata yutma; hepsi tek yerde. */
const request = (key, call, read = readItems) => {
  if (cache.has(key)) {
    return Promise.resolve(cache.get(key));
  }
  if (pending.has(key)) {
    return pending.get(key);
  }

  const promise = call()
    .then(read)
    .catch((error) => {
      console.error('Posta kodu servisi hatası:', error);
      return [];
    })
    .then((list) => {
      cache.set(key, list);
      pending.delete(key);
      return list;
    });

  pending.set(key, promise);
  return promise;
};

/**
 * Posta kodu araması. prefix / city / region opsiyonel ve önek eşleşir,
 * birlikte kullanılabilir. Hiçbiri verilmezse ülkenin ilk kodları döner.
 *
 * Dönen kayıtlar: { postalCode, city, region }
 */
export function searchPostalCodes({
  countryCode,
  prefix = '',
  city = '',
  region = '',
  limit = DEFAULT_LIMIT,
}) {
  if (!countryCode) {
    return Promise.resolve([]);
  }

  const normalizedPrefix = normalizePostalCode(prefix);
  const key = `search|${countryCode}|${normalizedPrefix}|${city}|${region}|${limit}`;

  return request(key, () =>
    Services.SearchPostalCodes({
      countryCode,
      prefix: normalizedPrefix,
      city,
      region,
      limit,
    })
  );
}

/**
 * Ülkenin bölgeleri (Türkiye'de il, ABD'de eyalet).
 * Dönen kayıtlar: { region, regionCode, total }
 */
export function getRegions({ countryCode, search = '', limit = 200 }) {
  if (!countryCode) {
    return Promise.resolve([]);
  }
  const key = `regions|${countryCode}|${search}|${limit}`;
  return request(key, () => Services.GetPostalCodeRegions({ countryCode, search, limit }));
}

/**
 * Şehir/semt listesi. region ile daraltılabilir.
 * Dönen kayıtlar: { city, region, total }
 */
export function getCities({ countryCode, region = '', search = '', limit = 200 }) {
  if (!countryCode) {
    return Promise.resolve([]);
  }
  const key = `cities|${countryCode}|${region}|${search}|${limit}`;
  return request(key, () => Services.GetPostalCodeCities({ countryCode, region, search, limit }));
}

/**
 * DHL'in tanıdığı şehirler (Address Validate). Posta kodu olmayan ülkelerde kullanılır;
 * dönen ad DHL'in yazımıdır (DUBAI) ve gönderide aynen kullanılmalıdır.
 * Dönen kayıtlar: { city, region } — region DHL hizmet bölgesi.
 */
export function getDhlCities({ type = 'delivery', countryCode, search = '' }) {
  const cityName = String(search).trim();
  if (!countryCode || !cityName) {
    return Promise.resolve([]);
  }
  const key = `dhl-cities|${type}|${countryCode}|${cityName.toLocaleUpperCase('en')}`;
  return request(
    key,
    () => Services.DHLValidateAddress({ type, countryCode, cityName }),
    (response) => {
      // Eşleşme yoksa servis status: false ve hata mesajı döndürüyor; boş liste sayılır.
      const list = response?.data?.status ? response.data.result?.address : [];
      return (Array.isArray(list) ? list : []).map((item) => ({
        city: item.cityName,
        region: item.serviceArea?.description || '',
      }));
    }
  );
}

/**
 * Posta kodu verisi bulunan ülkeler.
 */
export function getCountriesWithPostalCodes() {
  return request('countries', () => Services.GetPostalCodeCountries());
}

/** Önbelleği temizler; ülke verilirse yalnızca o ülkenin kayıtlarını siler. */
export function clearPostalCodeCache(countryCode) {
  if (!countryCode) {
    cache.clear();
    pending.clear();
    return;
  }
  [...cache.keys()]
    .filter((key) => key.includes(`|${countryCode}|`))
    .forEach((key) => cache.delete(key));
}